import React from 'react';

import { useIntl } from 'react-intl';

import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { queryClient } from 'src/lib/react-query';
import { toast } from 'src/hooks/useToast';
import { useUpdateUnit } from 'src/features/units/api/useUpdateUnit';
import { LIST_UNITS_RESOURCE } from 'src/features/units/api/useListUnits';
import { Unit } from 'src/features/units/utils/types';
import UnitFormModal from 'src/features/units/components/forms/UnitFormModal';
import { unitsMessages } from 'src/features/units/utils/messages';

interface UpdateUnitFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedUnit: Unit;
    children?: React.ReactNode;
}

function UpdateUnitFormModal<TData>({ className, semantics, children, selectedUnit }: UpdateUnitFormModalProps<TData>) {
    const { formatMessage } = useIntl();

    const { mutate, isPending } = useUpdateUnit({
        onSuccess: ({ name }) => {
            void queryClient.invalidateQueries({ queryKey: [LIST_UNITS_RESOURCE] });
            toast({
                title: formatMessage(unitsMessages.updateUnitSuccess),
                description: formatMessage(unitsMessages.updateUnitSuccessDescription, {
                    entity: name,
                }),
            });
        },
    });

    return (
        <UnitFormModal
            selectedUnit={selectedUnit}
            className={className}
            mutate={(values, onSuccess, onError) =>
                mutate({ ...values, unitId: selectedUnit.id }, { onSuccess, onError })
            }
            title={formatMessage(generalMessages.editEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(unitsMessages.editUnitSubtitle, {
                entity: selectedUnit.name,
            })}
        >
            {children}
        </UnitFormModal>
    );
}

export default UpdateUnitFormModal;
